import { Minus, Plus, Trash2 } from 'lucide-react';
import { cn, resolveImageUrl } from '@/lib/utils';
import { CartItem } from '@/contexts/cart-context';

import { Link } from 'react-router-dom';

type CartProductItemProps = {
  className?: string;
  item: CartItem;
  onUpdateQuantity: (quantity: number) => void;
  onRemove: () => void;
  onUpdateVariants?: (newVariants: Record<string, string>) => void;
};

export const CartProductItem = ({
  className,
  item,
  onUpdateQuantity,
  onRemove,
}: CartProductItemProps) => {
  const variantsDisplay = Object.values(item.selectedVariants).filter(Boolean).join(' / ') || null;

  return (
    <div className={cn('flex gap-4', className)}>
      {/* Product Image */}
      <Link
        to={`/products/${item.id}`}
        className="relative w-20 h-24 flex-shrink-0 bg-muted/30 overflow-hidden"
      >
        <img
          src={resolveImageUrl(item.media?.[0]?.url || '') || '/placeholder.png'}
          alt={item.label}
          className="absolute inset-0 object-cover"
        />
      </Link>

      {/* Product Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <Link
              to={`/products/${item.id}`}
              className="text-sm font-medium text-foreground line-clamp-1 hover:underline"
            >
              {item.label}
            </Link>
            {variantsDisplay && (
              <p className="text-xs text-muted-foreground mt-0.5">{variantsDisplay}</p>
            )}
          </div>
          <button
            onClick={onRemove}
            className="p-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Remove item"
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="flex items-center justify-between mt-3">
          {/* Quantity Controls */}
          <div className="flex items-center border border-border">
            <button
              onClick={() => onUpdateQuantity(Math.max(1, item.quantity - 1))}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Decrease quantity"
            >
              <Minus className="h-3 w-3" strokeWidth={1.5} />
            </button>
            <span className="w-8 text-center text-sm">{item.quantity}</span>
            <button
              onClick={() => onUpdateQuantity(item.quantity + 1)}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Increase quantity"
            >
              <Plus className="h-3 w-3" strokeWidth={1.5} />
            </button>
          </div>

          {/* Price */}
          <span className="text-sm font-medium">{(item.price * item.quantity).toFixed(2)} TND</span>
        </div>
      </div>
    </div>
  );
};
